import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { FLOORPLAN_VIEW, RESOURCES_VIEW_QUERY_PARAM } from 'floorplan/pages/native-floorplan/const';
import { useFloorplansQuery } from 'data/queries/useFloorplansQuery.hook';
import { useIsNativeFloorplanEnabled } from 'floorplan/hooks/use-is-native-floorplan-enabled.hook';
import { useAppInstanceConfigFeature } from 'hooks/use-app-instance-config-feature.hook';
import { selectCurrentLocationId } from 'store/app-instance-configs/selectors';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { searchParams as SearchParams } from 'shared/types/search-params';
import { Floorplan } from 'floorplan/pages/native-floorplan/types';

interface UseFloorplanViewStateValues {
  /**
   * The flag is on, the *currently selected* location is a native integration, and that location has
   * a floorplan to show. This is what the toggle, the map, and the map's filters hang on — offering a
   * switch to a map with nothing to draw, or to a vertical adapter that never had floorplans, would be
   * a dead end.
   */
  isFloorplanAvailable: boolean;
  isFloorplanView: boolean;
  floorplans: Floorplan[];
  /**
   * Whether to draw the redesigned resource card / full-width list layout. Only the flag — unlike
   * `isFloorplanAvailable` this does NOT require the current location to be native or to have a
   * floorplan: the list redesign ships everywhere the flag is on, the map/toggle is the part that is
   * conditional on top of that.
   */
  isRedesignedListEnabled: boolean;
}

/**
 * Whether the resources page is currently showing the native map, and whether it could.
 *
 * Split out of `useResourcesView` because three other places need the same answer from their own
 * subtrees — the filter bar swaps its control and its modal for the map's, the location field takes
 * the map's styling, and the list swaps its resource card — and none of them may pick up that hook's
 * navigation side effects to get it. The floorplans query is shared through TanStack Query, so asking
 * again costs no request.
 *
 * Floorplan availability is per *location*, not per session: the miniapp can launch into one building
 * and the user can switch to another through the location picker, so this re-resolves off the current
 * app instance config / location rather than the launch-time building.
 */
export const useFloorplanViewState = (): UseFloorplanViewStateValues => {
  const queryParams = useSearchParams<SearchParams>();
  const isNativeFloorplanEnabled = useIsNativeFloorplanEnabled();
  const { isNativeAdapter } = useAppInstanceConfigFeature();
  const currentLocationId = useSelector(selectCurrentLocationId);

  const selectedLocationIds = currentLocationId
    ? currentLocationId
        .split(',')
        .map(id => id.trim())
        .filter(Boolean)
    : [];
  const singleSelectedLocationId = selectedLocationIds.length === 1 ? selectedLocationIds[0] : null;

  // Floorplans only exist for the native integration — asking for any other adapter is a request
  // that can never succeed, so skip it rather than fetch and filter the empty result afterwards.
  const canLocationHaveFloorplan = isNativeFloorplanEnabled && isNativeAdapter;
  const canQueryFloorplans = canLocationHaveFloorplan && !!singleSelectedLocationId;

  const { floorplans } = useFloorplansQuery({
    buildingUuid: singleSelectedLocationId,
    enabled: canQueryFloorplans,
  });

  const isFloorplanAvailable = canQueryFloorplans && floorplans.length > 0;
  // A stale `view=floorplan` in the url must not strand the user on a map they cannot leave or that
  // has nothing to draw — including right after switching to a location with no floorplan.
  const isFloorplanView = isFloorplanAvailable && queryParams[RESOURCES_VIEW_QUERY_PARAM] === FLOORPLAN_VIEW;

  return useMemo(
    () => ({
      isFloorplanAvailable,
      isFloorplanView,
      floorplans,
      isRedesignedListEnabled: isNativeFloorplanEnabled,
    }),
    [isFloorplanAvailable, isFloorplanView, floorplans, isNativeFloorplanEnabled],
  );
};
